// =====================================================================
// Faz R (2026-05-06) — Cohort & Retention Analizi
// =====================================================================
// "Müşteri tutmak ne kadar iyi gidiyor" sorusunu cevaplar. Her ay kayıt
// olan müşteri grubunun (cohort) sonraki aylarda yüzde kaçının hala aktif
// olduğunu (sipariş verdiğini) heatmap'le gösterir.
//
// B2B'de en kıymetli analytics: müşteri kazanmak 5x pahalı, tutmak ucuz.
// Aylık retention curve = LTV doğrudan göstergesi.
//
// Endpoint: GET /api/admin/insights/cohort?months=12
//
// Yorumlama:
//   - 1. ay (offset 0) %100 — cohort kendisi, baseline
//   - 2. ay %30-50 — sektör tipik (B2B plastik)
//   - 3. ay %25-40 — kademeli erime
//   - 6+ ay %20+ kalıyorsa "sadık çekirdek" var
//   - Cohort'lar arası karşılaştırma: yeni cohort retention eski cohort'tan
//     iyi mi (ürün/servis iyileşiyor mu)?
// =====================================================================
import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Card, Typography, Grid, Chip, CircularProgress, Alert,
  IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Table, TableHead,
  TableRow, TableCell, TableBody
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import API from "../services/api";

const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");

// Yüzdelik değer → arka plan rengi (heatmap)
// %100 = koyu yeşil; %50 = sarı; %0 = kırmızıya yakın
const heatColor = (pct) => {
  if (pct == null || pct === 0) return "#f1f5f9";
  if (pct >= 80) return "#10b981";
  if (pct >= 60) return "#22c55e";
  if (pct >= 40) return "#84cc16";
  if (pct >= 25) return "#f59e0b";
  if (pct >= 10) return "#fb923c";
  return "#ef4444";
};

// Hex'e ne kadar opacity (yüzdelik orana göre)
const heatOpacity = (pct) => {
  if (pct == null || pct === 0) return 1;
  return Math.max(0.3, Math.min(1, pct / 100));
};

// "2026-05" → "May 26"
const cohortLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const months = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

export default function CohortRetention() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [months, setMonths] = useState(12);

  const load = (m = months) => {
    setLoading(true);
    API.get(`/api/admin/insights/cohort?months=${m}`)
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(months); }, [months]);

  // Max offset (matrix kolon sayısı) — en eski cohort'un yaşadığı offset
  const maxOffset = useMemo(() => {
    if (!data?.cohorts || data.cohorts.length === 0) return 0;
    let max = 0;
    for (const c of data.cohorts) {
      const offsets = Object.keys(c.retention || {}).map(Number);
      if (offsets.length > 0) max = Math.max(max, ...offsets);
    }
    return max;
  }, [data]);

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  // Cohort sıralaması: en yeni üstte
  const sortedCohorts = [...(data.cohorts || [])].reverse();

  // Avg retention — 1. aydan sonraki ortalama (1, 2, 3...)
  const avgEntries = Object.entries(data.avg_retention || {})
    .map(([k, v]) => [parseInt(k, 10), Number(v) || 0])
    .sort((a, b) => a[0] - b[0]);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Cohort & Retention</Typography>
          <Typography variant="body2" color="text.secondary">
            Hangi ay kayıt olan müşteri, sonraki aylarda hala aktif?
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup
            size="small" value={months} exclusive
            onChange={(_, v) => v && setMonths(v)}
          >
            <ToggleButton value={6}>6 ay</ToggleButton>
            <ToggleButton value={12}>12 ay</ToggleButton>
            <ToggleButton value={24}>24 ay</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Yenile">
            <IconButton onClick={() => load(months)}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Özet KPI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Cohort Sayısı</Typography>
            <Typography variant="h4" fontWeight={800}>{data.cohorts?.length || 0}</Typography>
            <Typography variant="caption" color="text.disabled">son {months} ay</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Müşteri</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">{fmtNum(data.total_users)}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Ortalama Aylık Retention</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">
              %{(avgEntries[1]?.[1] || 0).toFixed(1)}
            </Typography>
            <Typography variant="caption" color="text.disabled">2. ay (kayıt+1)</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Uzun Vade Retention</Typography>
            <Typography variant="h4" fontWeight={800} color="#f59e0b">
              %{(avgEntries[5]?.[1] || avgEntries[avgEntries.length - 1]?.[1] || 0).toFixed(1)}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {avgEntries[5] ? "6. ay" : `${avgEntries.length}. ay`}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Cohort heatmap matrisi */}
      <Card sx={{ borderRadius: 3, overflow: "auto" }}>
        <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" fontWeight={700}>Retention Heatmap</Typography>
          <Typography variant="caption" color="text.secondary">
            Her satır bir cohort (kayıt ayı). Her hücre o cohort'un N. ayda hala aktif olan üyesinin yüzdesi.
            Renk koyulaştıkça retention iyi.
          </Typography>
        </Box>
        {sortedCohorts.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">Bu süre aralığında cohort yok</Alert>
          </Box>
        ) : (
          <Table size="small" sx={{ minWidth: maxOffset > 6 ? 800 : "auto" }}>
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ position: "sticky", left: 0, bgcolor: "#f8fafc", zIndex: 1 }}>
                  <strong>Kayıt Ayı</strong>
                </TableCell>
                <TableCell align="center"><strong>Üye</strong></TableCell>
                {Array.from({ length: maxOffset + 1 }, (_, i) => (
                  <TableCell key={i} align="center" sx={{ minWidth: 60 }}>
                    <strong>+{i}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCohorts.map(c => (
                <TableRow key={c.cohort_month} hover>
                  <TableCell sx={{ position: "sticky", left: 0, bgcolor: "white", fontWeight: 700 }}>
                    {cohortLabel(c.cohort_month)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{fmtNum(c.cohort_size)}</TableCell>
                  {Array.from({ length: maxOffset + 1 }, (_, i) => {
                    const r = c.retention[i];
                    const pct = r ? r.pct : null;
                    const bg = heatColor(pct);
                    const op = heatOpacity(pct);
                    return (
                      <TableCell
                        key={i} align="center"
                        sx={{
                          bgcolor: bg, opacity: op,
                          color: pct >= 25 ? "white" : "#1f2937",
                          fontWeight: 700, fontSize: 12,
                          borderLeft: "1px solid white"
                        }}
                        title={r ? `${r.active}/${c.cohort_size} aktif` : "veri yok"}
                      >
                        {pct == null ? "—" : `${pct}%`}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Ortalama retention curve */}
      {avgEntries.length > 1 && (
        <Card sx={{ p: 2.5, borderRadius: 3, mt: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>Ortalama Retention Curve</Typography>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 100 }}>
            {avgEntries.map(([offset, pct]) => (
              <Tooltip key={offset} title={`${offset}. ay (kayıt+${offset}): ortalama %${pct}`} placement="top">
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: 10, color: "#1f2937", fontWeight: 700 }}>
                    {pct}%
                  </Typography>
                  <Box sx={{
                    width: "100%",
                    height: `${Math.max(pct, 2)}%`,
                    bgcolor: heatColor(pct),
                    opacity: heatOpacity(pct),
                    borderRadius: "3px 3px 0 0"
                  }} />
                  <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled" }}>
                    +{offset}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            💡 Yorumlama: 1. ay (kayıt) %100 baseline. Sonraki aylar düşer — sektör tipik B2B plastik:
            2. ay %30-50 normal, 6+ ay %20 üzeri sadık çekirdek anlamına gelir. Cohort'lar arası karşılaştırma yap:
            yeni cohort eski cohort'tan iyi mi → ürün iyileşiyor.
          </Typography>
        </Card>
      )}
    </Box>
  );
}
